import React, { useState, useEffect } from "react";
import {
  Card,
  Container,
  Typography,
  TextField,
  Box,
  Button,
  Stack,
  FormGroup,
  FormControlLabel,
  Switch,
  FormControl,
  CircularProgress
} from "@mui/material";
import Page from "../components/Page";
import { addUpdateApplicationRouteApi, getApplicationRouteApi } from "src/api/commonapi";
import { notification } from "src/utils/messages";
import Grid from "@mui/material/Grid";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { Routes } from "src/constant/api_url";
import { displayFilter } from "src/constant/variable";
import Select from "react-select";
import CustomLoader from "src/commonComponents/CustomLoader";
import normalizeSearch from "src/utils/normalizeSearch";

export default function HowToUseApplicationView() {
  const [howToUseApplicationData, setHowToUseApplicationData] = useState([]);
  const [updatedRouteData, setUpdatedRouteData] = useState([]);
  const [updatedPdf, setUpdatedPdf] = useState({});
  const [newRouteDataList, setNewRouteDataList] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [filtereddata, setFilteredData] = useState({
    search: "",
    display: -1
  });

  const renderFileSize = (size) => {
    if (!size) return "";
    if (Math.round(size / 100) / 10 > 1000) {
      return `${(size / 1024 / 1024).toFixed(2)} mb`;
    }
    return `${(size / 1024).toFixed(2)} kb`;
  };

  const handleRemovePdf = (route) => {
    const pdfKey = `pdf[${route?.a_r_id}]`;

    setUpdatedPdf((prev) => {
      const copy = { ...prev };
      if (copy[pdfKey]) {
        delete copy[pdfKey];
      }
      return copy;
    });

    setUpdatedRouteData((prev) => {
      const exists = prev.some((item) => item.a_r_id === route.a_r_id);

      if (exists) {
        return prev.map((item) => (item.a_r_id === route.a_r_id ? { ...item, pdf_url: "" } : item));
      }

      return [...prev, { ...route, pdf_url: "" }];
    });

    setNewRouteDataList((prev) => {
      return prev.map((item) => (item.a_r_id === route.a_r_id ? { ...item, pdf_url: "" } : item));
    });
  };

  const getApplicationRoute = async () => {
    setIsLoading(true);
    const response = await getApplicationRouteApi({ type: 1 });
    setIsLoading(false);
    let data = response?.data || [];

    setUpdatedRouteData([]);
    setUpdatedPdf({});
    setHowToUseApplicationData(data);
  };

  const handleDataChange = (field, value, a_r_id, route, ind = 0) => {
    setUpdatedRouteData((prevData) => {
      const existingUpdatedData = prevData || [];

      const existingIndex = existingUpdatedData.findIndex((item) => item.a_r_id == a_r_id);

      let updatedPageData;

      if (existingIndex >= 0) {
        if (field == "pdf_url")
          setUpdatedPdf((prevData) => ({ ...prevData, [`pdf[${route?.a_r_id}]`]: value }));

        updatedPageData = [...existingUpdatedData];

        updatedPageData[existingIndex] = {
          ...updatedPageData[existingIndex],
          [field]: field == "pdf_url" ? route?.pdf_url : value
        };
      } else {
        if (field == "pdf_url")
          setUpdatedPdf((prevData) => ({ ...prevData, [`pdf[${route?.a_r_id}]`]: value }));

        updatedPageData = [
          ...existingUpdatedData,
          {
            ...route,
            [field]: field == "pdf_url" ? route?.pdf_url : value
          }
        ];
      }

      return updatedPageData;
    });
  };

  const handleSaveAllData = async () => {
    const formData = new FormData();

    formData.append("app_route", JSON.stringify(updatedRouteData));

    Object.entries(updatedPdf).forEach(([key, value]) => {
      formData.append(key, value);
    });

    setIsLoading(true);
    await addUpdateApplicationRouteApi(formData).then((response) => {
      let notify = notification({
        type: response.status == 0 ? "error" : "success",
        message: response.message,
        position: "top"
      });
      setIsLoading(false);
      notify();
      getApplicationRoute();
    });
  };

  useEffect(() => {
    getApplicationRoute();
  }, []);

  const filterData = () => {
    const filterBySearchType = (route) => {
      let searchFilterText = filtereddata.search.toLowerCase();

      // 🔎 Search Filter
      const searchMatch =
        filtereddata.search == "" || route?.screen_name?.toLowerCase().includes(normalizeSearch(searchFilterText));

      // 🔎 Display Filter
      const displayMatch = filtereddata.display == -1 || route?.status == filtereddata.display;

      return searchMatch && displayMatch;
    };

    const nROUTE = howToUseApplicationData.filter((route) => filterBySearchType(route));

    setNewRouteDataList(nROUTE);
  };

  useEffect(() => {
    filterData();
  }, [filtereddata, howToUseApplicationData]);

  return (
    <Page title="How To Use Application | Local Vocal Business Group">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="h4" gutterBottom>
            How To Use Application
          </Typography>

          <Stack direction="row" spacing={1} ml="auto" alignItems="end">
            <Stack spacing={1} sx={{ width: "150px" }}>
              <label>Select Display</label>

              <Select
                options={displayFilter}
                value={displayFilter.find((option) => option.value === filtereddata.display)}
                onChange={(selectedOption) => {
                  setFilteredData((prev) => ({
                    ...prev,
                    display: selectedOption?.value
                  }));
                }}
                menuPortalTarget={document.body}
              />
            </Stack>

            <TextField
              size="small"
              label="Search Route"
              value={filtereddata?.search}
              onChange={(e) => setFilteredData({ ...filtereddata, search: e.target.value })}
            />
            <Button
              onClick={() => {
                setFilteredData({
                  search: "",
                  display: -1
                });
              }}
              variant="contained"
            >
              clear
            </Button>

            <Button
              variant="outlined"
              onClick={handleSaveAllData}
              disabled={isLoading}
              startIcon={isLoading && <CircularProgress size={20} color="inherit" />}
            >
              Save
            </Button>
          </Stack>
        </Stack>

        <Card
          sx={{
            height: "calc(100vh - 220px)",
            overflow: "hidden",
            borderRadius: "12px 0px 0px 12px"
          }}
        >
          <Box
            sx={{
              p: 3,
              height: "100%",
              overflowY: "auto"
            }}
          >
            <Grid container spacing={3}>
              {newRouteDataList.map((route, ind) => (
                <Grid
                  item
                  xs={12}
                  md={12}
                  lg={12}
                  key={route?.screen_route?.replace("/", "") || ""}
                >
                  <Card variant="outlined" sx={{ p: 2, height: "100%" }}>
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between"
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {ind + 1}
                          {". "}
                          {route?.screen_name || ""}
                        </Typography>

                        <FormControl component="fieldset">
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Switch
                                  name="display"
                                  checked={
                                    Number(
                                      updatedRouteData?.find(
                                        (item) => item?.a_r_id == route?.a_r_id
                                      )?.status ?? route.status
                                    ) === 1
                                  }
                                  onChange={(e) => {
                                    const val = e.target.checked ? 1 : 0;
                                    handleDataChange("status", val, route?.a_r_id, route);
                                  }}
                                />
                              }
                              label="Display"
                            />
                          </FormGroup>
                        </FormControl>
                      </Box>
                    </Stack>
                    <Stack spacing={2} sx={{ pt: 2 }}>
                      <Grid container>
                        <Grid item xs={12} sm={6} sx={{ pr: { sm: 1 }, mb: { xs: 2, sm: 0 } }}>
                          <label>Youtube Video Title</label>
                          <TextField
                            fullWidth
                            placeholder="Youtube Video Title"
                            value={
                              updatedRouteData?.find((item) => item?.a_r_id == route?.a_r_id)
                                ?.youtube_title ?? route?.youtube_title
                            }
                            onChange={(e) =>
                              handleDataChange(
                                "youtube_title",
                                e.target.value,
                                route?.a_r_id,
                                route
                              )
                            }
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <label>Youtube Video URL</label>
                          <TextField
                            fullWidth
                            placeholder="Youtube Video URL"
                            value={
                              updatedRouteData?.find((item) => item?.a_r_id == route?.a_r_id)
                                ?.youtube_url ?? route?.youtube_url
                            }
                            onChange={(e) =>
                              handleDataChange("youtube_url", e.target.value, route?.a_r_id, route)
                            }
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                    <Stack spacing={2} sx={{ pt: 2 }}>
                      <Grid container>
                        <Grid item xs={12} sm={6} sx={{ pr: { sm: 1 }, mb: { xs: 2, sm: 0 } }}>
                          <label>PDF Title</label>
                          <TextField
                            fullWidth
                            placeholder="PDF Title"
                            value={
                              updatedRouteData?.find((item) => item?.a_r_id == route?.a_r_id)
                                ?.pdf_title ?? route?.pdf_title
                            }
                            onChange={(e) =>
                              handleDataChange("pdf_title", e.target.value, route?.a_r_id, route)
                            }
                            variant="outlined"
                            size="small"
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end"
                          }}
                        >
                          <Button variant="contained" component="label" sx={{ height: 40 }}>
                            Upload File
                            <input
                              id="file"
                              name="pdf_url"
                              type="file"
                              hidden
                              accept=".pdf"
                              onChange={(e) => {
                                const file = e.target.files[0];

                                if (!file) return;
                                handleDataChange("pdf_url", file, route?.a_r_id, route, ind);
                              }}
                            />
                          </Button>
                        </Grid>
                        {(updatedPdf[`pdf[${route?.a_r_id}]`] ??
                          newRouteDataList[ind]?.pdf_url) && (
                          <Box
                            sx={{
                              mt: 1,
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              border: "1px solid #e0e0e0",
                              borderRadius: 1,
                              p: 1
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                                minWidth: 0
                              }}
                            >
                              <PictureAsPdfIcon
                                color="error"
                                fontSize="large"
                                sx={{ flexShrink: 0 }}
                              />

                              <Box sx={{ ml: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {updatedPdf[`pdf[${route?.a_r_id}]`]?.name ||
                                    newRouteDataList[ind]?.pdf_url?.split("/").pop()}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">
                                  {renderFileSize(updatedPdf[`pdf[${route?.a_r_id}]`]?.size || 0)}
                                </Typography>
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1px",
                                flexShrink: 0
                              }}
                            >
                              <Button
                                size="small"
                                onClick={() => {
                                  const file = updatedPdf[`pdf[${route?.a_r_id}]`];
                                  const pdfUrl = newRouteDataList[ind]?.pdf_url;

                                  if (file instanceof File) {
                                    const fileURL = URL.createObjectURL(file);
                                    window.open(fileURL, "_blank");
                                  } else if (pdfUrl) {
                                    window.open(
                                      `${Routes.API_HOSTNAME}application-routes/${pdfUrl}`,
                                      "_blank"
                                    );
                                  }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>

                              <Button
                                size="small"
                                color="error"
                                onClick={() => handleRemovePdf(route)}
                              >
                                <CloseIcon fontSize="small" />
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Grid>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {newRouteDataList.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  {isLoading ? (
                    <CustomLoader />
                  ) : (
                    "No routes found. Please check the route configuration."
                  )}
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Container>
    </Page>
  );
}
